import { Component, computed, inject, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PermissionsService } from '../../../services/permissions.service';

export type ButtonVariant =
  | 'primary'
  | 'primary-clean'
  | 'secondary'
  | 'danger'
  | 'danger-light'
  | 'dangersoft'
  | 'ghost'
  | 'light-primary'
  | 'confirm'
  | 'cancel'
  | 'clean-danger'
  | 'light';
export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg';

@Component({
  selector: 'lib-custom-button',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './custom-button.component.html',
})
export class CustomButtonComponent {
  private _permissionsService = inject(PermissionsService);

  // Inputs
  label = input<string>('');
  variant = input<ButtonVariant>('primary');
  /**
   * FontAwesome icon class (e.g., 'fa-plus', 'fa-user', etc.)
   * If provided, will display the icon before the label
   */
  icon = input<string>('');
  size = input<ButtonSize>('md');
  type = input<'button' | 'submit' | 'reset'>('button');
  disabled = input<boolean>(false);
  loading = input<boolean>(false);
  /**
   * If true, the button will take the full available width (adds `w-full`)
   */
  fullWidth = input<boolean>(false);

  /**
   * Permissions required to view this button.
   * If provided, the button will only be visible if the user has the required permissions.
   */
  permissions = input<string[][]>();

  // Outputs
  clicked = output<void>();

  // Computed signals
  isDisabled = computed(() => this.disabled() || this.loading());

  hasPermission = computed(() => this._permissionsService.hasPermissions(this.permissions()));

  // Computed signal for dynamic classes
  buttonClasses = computed(() => {
    const baseClasses = [
      'cursor-pointer',
      'inline-flex',
      'items-center',
      'justify-center',
      'border',
      'font-medium',
      'rounded-[11px]',
      'focus:outline-none',
      'focus:ring-2',
      'focus:ring-offset-2',
      'transition-colors',
      'duration-150',
      this.label() ? 'min-w-[100px]' : '', // Ancho mínimo para textos cortos
    ];

    const variantClasses = {
      primary:
        'shadow-sm bg-primary text-white hover:bg-primary/80 focus:ring-primary/80 border-transparent',
      cancel:
        'shadow-sm bg-primary_light text-primary_dark hover:bg-primary_light/80 focus:ring-primary_dark/80 border-transparent',
      confirm:
        'shadow-sm bg-confirm text-white hover:bg-confirm/80 focus:ring-confirm/80 border-transparent',
      secondary:
        'shadow-sm bg-white text-gray-800 hover:bg-gray-50 focus:ring-gray-300 border-gray-800',
      dangersoft:
        'shadow-sm bg-error_container_light text-error_on focus:ring-error_container_light border-transparent',
      danger:
        'shadow-sm bg-error text-white hover:bg-error_light focus:ring-error_light border-transparent',
      'danger-light': 'bg-transparent text-error focus:ring-error/80 border-transparent',
      'clean-danger':
        'shadow-sm bg-transparent text-error hover:bg-error_light focus:ring-error_light border-0 shadow-none',
      ghost:
        'shadow-sm bg-transparent text-gray-700 hover:bg-gray-100 focus:ring-gray-500 border-gray-300',
      'light-primary':
        'shadow-sm bg-sky-100 text-sky-700 hover:bg-sky-200 focus:ring-sky-300 border-sky-500',
      light:
        'shadow-sm bg-primary_container text-primary_dark focus:ring-primary_dark border-primary_container',
      'primary-clean':
        'bg-transparent text-primary hover:bg-primary_light focus:ring-primary_light border-0 shadow-none',
    };

    const sizeClasses = {
      xs: this.label() ? 'px-1.5 py-1 text-[8px]' : 'text-[8px]',
      sm: this.label() ? 'px-2.5 py-1.5 text-[12px]' : 'text-[12px]',
      md: this.label() ? 'px-4 py-2 text-[14px]' : 'text-[14px]',
      lg: this.label() ? 'px-6 py-3 text-base' : 'text-base',
    };

    const disabledClasses = 'opacity-50 cursor-not-allowed';

    const widthClass = this.fullWidth() ? 'w-full' : '';

    return [
      ...baseClasses,
      variantClasses[this.variant()],
      sizeClasses[this.size()],
      this.isDisabled() ? disabledClasses : '',
      widthClass,
    ].join(' ');
  });

  onClick(): void {
    if (!this.isDisabled()) {
      this.clicked.emit();
    }
  }
}
